import { useState } from 'react'
import { Pencil, Trash2, X, Check } from 'lucide-react'
import type { Comment } from './types'

interface CommentThreadProps {
  comments: Comment[]
  onAddComment?: (message: string) => void
  onEditComment?: (commentId: string, message: string) => void
  onDeleteComment?: (commentId: string) => void
}

interface CommentItemProps {
  comment: Comment
  onEdit?: (commentId: string, message: string) => void
  onDelete?: (commentId: string) => void
}

function CommentItem({ comment, onEdit, onDelete }: CommentItemProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editMessage, setEditMessage] = useState(comment.message)

  const formattedDate = new Date(comment.createdAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

  // Get initials for avatar
  const initials = comment.authorName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  const handleSaveEdit = () => {
    if (editMessage.trim() && onEdit) {
      onEdit(comment.id, editMessage.trim())
      setIsEditing(false)
    }
  }

  const handleCancelEdit = () => {
    setEditMessage(comment.message)
    setIsEditing(false)
  }

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this comment?') && onDelete) {
      onDelete(comment.id)
    }
  }

  return (
    <div className="flex gap-3 group">
      {/* Avatar */}
      <div className="shrink-0 w-9 h-9 rounded-full bg-teal-100 dark:bg-teal-900 flex items-center justify-center mt-0.5">
        <span className="text-xs font-semibold text-teal-700 dark:text-teal-300">{initials}</span>
      </div>

      <div className="flex-1 min-w-0 relative">
        {/* Header */}
        <div className="flex items-baseline gap-2 mb-1">
          <span className="text-sm font-medium text-stone-900 dark:text-stone-100 leading-tight">
            {comment.authorName}
          </span>
          <span className="text-xs text-stone-400 dark:text-stone-500">{formattedDate}</span>
        </div>

        {/* Action buttons - positioned absolutely to not affect layout */}
        {!isEditing && (comment.canEdit || comment.canDelete) && (
          <div className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity">
            {comment.canEdit && (
              <button
                onClick={() => setIsEditing(true)}
                className="p-1 text-stone-400 hover:text-teal-500 transition-colors"
                title="Edit comment"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
            {comment.canDelete && (
              <button
                onClick={handleDelete}
                className="p-1 text-stone-400 hover:text-red-500 transition-colors"
                title="Delete comment"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}

        {/* Message or Edit form */}
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              value={editMessage}
              onChange={(e) => setEditMessage(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 text-sm rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 resize-none"
            />
            <div className="flex gap-2">
              <button
                onClick={handleSaveEdit}
                disabled={!editMessage.trim()}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-teal-500 text-white hover:bg-teal-600 disabled:opacity-50 transition-colors"
              >
                <Check className="w-3 h-3" />
                Save
              </button>
              <button
                onClick={handleCancelEdit}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-stone-100 dark:bg-stone-700 text-stone-600 dark:text-stone-300 hover:bg-stone-200 dark:hover:bg-stone-600 transition-colors"
              >
                <X className="w-3 h-3" />
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-stone-700 dark:text-stone-300 leading-relaxed">
            {comment.message}
          </p>
        )}
      </div>
    </div>
  )
}

export function CommentThread({ comments, onAddComment, onEditComment, onDeleteComment }: CommentThreadProps) {
  const [newComment, setNewComment] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (newComment.trim() && onAddComment) {
      onAddComment(newComment.trim())
      setNewComment('')
    }
  }

  return (
    <div>
      {/* Comment list */}
      {comments.length > 0 ? (
        <div className="space-y-5 mb-6">
          {comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              onEdit={onEditComment}
              onDelete={onDeleteComment}
            />
          ))}
        </div>
      ) : (
        <p className="text-sm text-stone-500 dark:text-stone-400 italic mb-6">
          No comments yet. Be the first to start the discussion.
        </p>
      )}

      {/* Add comment form */}
      <form onSubmit={handleSubmit} className="flex gap-3 items-start">
        <div className="shrink-0 w-9 h-9 rounded-full bg-stone-200 dark:bg-stone-700 flex items-center justify-center">
          <span className="text-xs font-semibold text-stone-600 dark:text-stone-300">You</span>
        </div>
        <div className="flex-1">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment..."
            rows={2}
            className="w-full px-4 py-3 text-sm rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 placeholder-stone-400 dark:placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 dark:focus:ring-teal-400/20 dark:focus:border-teal-400 resize-none transition-colors"
          />
          <div className="flex justify-end mt-2">
            <button
              type="submit"
              disabled={!newComment.trim()}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-teal-500 hover:bg-teal-600 dark:bg-teal-600 dark:hover:bg-teal-500 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Post Comment
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
